import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from 'components/styleguide/button/Button';

const styles = require('./EorTooltip.module.scss');
const iconInfo = require('svg/icons-lj-styleguide-2025/info.svg');

const CLOSE_DELAY_MS = 150;
const VIEWPORT_MARGIN = 16;
const GAP = 8;

let tooltipIdCounter = 0;

export default function EorTooltip() {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [id] = useState(() => `eor-tooltip-${++tooltipIdCounter}`);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const popoverId = `${id}-popover`;
  const titleId = `${id}-title`;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);

  useEffect(() => () => clearCloseTimer(), []);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const reposition = () => {
      if (!triggerRef.current || !popoverRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      const idealLeft = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
      const maxLeft = window.innerWidth - popoverRect.width - VIEWPORT_MARGIN;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, maxLeft));

      const aboveTop = triggerRect.top - GAP - popoverRect.height;
      const belowTop = triggerRect.bottom + GAP;
      const top = aboveTop >= VIEWPORT_MARGIN ? aboveTop : belowTop;

      setPosition({ top, left });
    };

    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleTryEor = () => {
    document.dispatchEvent(new Event('eor-calculator:open'));
    setOpen(false);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Tab' && !event.shiftKey && open && popoverRef.current) {
      const button = popoverRef.current.querySelector<HTMLButtonElement>('button');
      if (button) {
        event.preventDefault();
        button.focus();
      }
    }
  };

  const handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      triggerRef.current?.focus();
    }
  };

  return (
    <span className={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label="More info about Employer of Record"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={handleTriggerKeyDown}
      >
        <img src={iconInfo} alt="" width="18" height="18" />
      </button>
      {open && ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          ref={popoverRef}
          id={popoverId}
          className={styles.popover}
          style={{
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            visibility: position ? 'visible' : 'hidden',
          }}
          role="dialog"
          aria-labelledby={titleId}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
          onKeyDown={handlePopoverKeyDown}
        >
          <h4 id={titleId} className={styles.title}>Hiring internationally?</h4>
          <p className={styles.text}>You might need Employee of Record (EOR).</p>
          <Button context="neutral" type="primary" size="M" onClick={handleTryEor}>
            Try EOR
          </Button>
        </div>,
        document.body
      )}
    </span>
  );
}
